import React, { FC, useState, useEffect } from 'react';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Popup } from '@components/atoms/Popup';
import { useTranslation } from 'react-i18next';
import modalStyles from '@components/molecules/PopupDelete/PopupDelete.module.css';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { useGetAllMyAccountStores } from '@services/useGetAllMyAccountStores';
import { useGetMyAccountProducts } from '@services/useGetMyAccountProducts';
import { useGetMyAccountServices } from '@services/useGetMyAccountServices';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useDeleteUser } from '@services/useDeleteUser';
import { useGetPendingMyStoresOrdersCount } from '@services/useGetPendingMyStoresOrdersCount';
import { useGetPendingMyOrdersCount } from '@services/useGetPendingMyOrdersCount';
import { useGetMyProductReservationsCount } from '@services/useGetMyProductReservationsCount';
import { useNotifications } from '@hooks/useNotifications';

export const DeleteAccount: FC = () => {
    const { t } = useTranslation();
    const { navigate } = useCustomNavigate();
    const { user } = useData();
    const { showDefaultNotification } = useNotifications();
    const { request: requestDeleteUser, loading: loadingDeleteUser, error: errorDeleteUser, lastResponseTime, succeeded } = useDeleteUser();
    const [showDeleteInfo, setShowDeleteInfo] = useState(false);
    const [showIssuesModal, setShowIssuesModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [checkingIssues, setCheckingIssues] = useState(false);
    const [confirmAcknowledge, setConfirmAcknowledge] = useState(false);
    const { request: requestAllMyAccountStores } = useGetAllMyAccountStores();
    const { request: requestMyAccountProducts } = useGetMyAccountProducts();
    const { request: requestMyAccountServices } = useGetMyAccountServices();
    const {
        allMyAccountStores,
        isLoadingAllMyAccountStores,
        myAccountProducts,
        isLoadingMyAccountProducts,
        myAccountServices,
        isLoadingMyAccountServices,
    } = useData();

    const [pendingOrdersCount, setPendingOrdersCount] = useState<number | null>(null);
    const [consumerOrdersCount, setConsumerOrdersCount] = useState<number | null>(null);
    const [consumerReservationsCount, setConsumerReservationsCount] = useState<number | null>(null);
    const [productsCount, setProductsCount] = useState<number | null>(null);
    const [servicesCount, setServicesCount] = useState<number | null>(null);
    const [storeNames, setStoreNames] = useState<string[] | null>(null);
    const { request: requestPendingMyStoresOrdersCount } = useGetPendingMyStoresOrdersCount();
    const { request: requestPendingMyOrdersCount } = useGetPendingMyOrdersCount();
    const { request: requestMyProductReservationsCount } = useGetMyProductReservationsCount();

    const translations = {
        title: t('delete-account-component.title'),
        intro: t('delete-account-component.intro'),
        sectionDeleteTitle: t('delete-account-component.section-delete.title'),
        sectionDeleteItem1: t('delete-account-component.section-delete.items.data'),
        sectionDeleteItem2: t('delete-account-component.section-delete.items.visibility'),
        sectionDeleteItem3: t('delete-account-component.section-delete.items.content'),
        sectionKeepTitle: t('delete-account-component.section-keep.title'),
        sectionKeepText: t('delete-account-component.section-keep.text'),
        sectionBeforeTitle: t('delete-account-component.section-before.title'),
        sectionBeforeItem1: t('delete-account-component.section-before.items.orders'),
        sectionBeforeItem2: t('delete-account-component.section-before.items.payments'),
        sectionBeforeItem3: t('delete-account-component.section-before.items.disputes'),
        sectionAfterText1: t('delete-account-component.section-after.text1'),
        sectionAfterText2: t('delete-account-component.section-after.text2'),
        buttonDelete: t('delete-account-component.button'),
        modalTitle: t('delete-account-component.modal.title'),
        modalItemPendingOrders: t('delete-account-component.modal.items.pendingOrders'),
        modalItemAssociatedStores: t('delete-account-component.modal.items.associatedStores'),
        modalItemProductCount: t('delete-account-component.modal.items.productCount'),
        modalItemServiceCount: t('delete-account-component.modal.items.serviceCount'),
        modalContinue: t('delete-account-component.modal.continue'),
        modalLoading: t('delete-account-component.modal.loading'),
        modalNoStores: t('delete-account-component.modal.noStores'),
        issuesTitle: (count: number) => t('delete-account-component.issues.title', { count }),
        issuesSubtitle: t('delete-account-component.issues.subtitle'),
        issuesBack: t('delete-account-component.issues.back'),
        issuesOrdersAsMerchant: t('delete-account-component.issues.orders-as-merchant'),
        issuesOrdersAsConsumer: t('delete-account-component.issues.orders-as-consumer'),
        issuesReservationsAsConsumer: t('delete-account-component.issues.reservations-as-consumer'),
        confirmTitle: t('delete-account-component.confirm.title'),
        confirmText: t('delete-account-component.confirm.text'),
        confirmWarning: t('delete-account-component.confirm.warning'),
        confirmAcknowledge: t('delete-account-component.confirm.acknowledge'),
        confirmCancel: t('delete-account-component.confirm.cancel'),
        confirmCTA: t('delete-account-component.confirm.cta'),
    };

    // derive counts and lists from context
    useEffect(() => {
        if (myAccountProducts) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const total = (myAccountProducts as any)?.pagination?.total ?? (myAccountProducts?.data?.length || 0);
            setProductsCount(typeof total === 'number' ? total : Number(total) || 0);
        }
    }, [myAccountProducts]);

    useEffect(() => {
        if (myAccountServices) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const total = (myAccountServices as any)?.pagination?.total ?? (myAccountServices?.data?.length || 0);
            setServicesCount(typeof total === 'number' ? total : Number(total) || 0);
        }
    }, [myAccountServices]);

    useEffect(() => {
        if (allMyAccountStores) {
            const names = (allMyAccountStores?.data || [])
                .map((s) => s?.name || '')
                .filter((n): n is string => Boolean(n));
            setStoreNames(names);
        }
    }, [allMyAccountStores]);

    // when opening modal, fetch the data needed
    useEffect(() => {
        if (!showDeleteInfo) return;

        !allMyAccountStores && !isLoadingAllMyAccountStores && requestAllMyAccountStores();
        !myAccountProducts && !isLoadingMyAccountProducts && requestMyAccountProducts({ page: 1, per_page: 1 });
        !myAccountServices && !isLoadingMyAccountServices && requestMyAccountServices({ page: 1, per_page: 1 });

        const run = async () => {
            try {
                setPendingOrdersCount(null);
                const count = await requestPendingMyStoresOrdersCount();
                setPendingOrdersCount(count);
            } catch {
                setPendingOrdersCount(0);
            }
        };

        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDeleteInfo]);

    const onContinueFromInfo = async () => {
        setCheckingIssues(true);

        try {
            const merchantIssues = pendingOrdersCount ?? 0;
            const [consumerOrdersIssues, consumerReservationsIssues] = await Promise.all([
                (async () => {
                    const c = await requestPendingMyOrdersCount();
                    setConsumerOrdersCount(c);
                    return c;
                })(),
                (async () => {
                    const c = await requestMyProductReservationsCount();
                    setConsumerReservationsCount(c);
                    return c;
                })(),
            ]);

            const totalIssues = (merchantIssues || 0) + (consumerOrdersIssues || 0) + (consumerReservationsIssues || 0);

            setShowDeleteInfo(false);
            if (totalIssues > 0) {
                setShowIssuesModal(true);
            } else {
                setShowConfirmModal(true);
            }
        } finally {
            setCheckingIssues(false);
        }
    };

    useEffect(() => {
        if (!lastResponseTime || succeeded === null) return;
        if (succeeded === true) {
            setShowConfirmModal(false);
            setShowDeleteInfo(false);
            navigate(`/${SITE_URLS.deleteAccountCompleted}`);
        } else if (succeeded === false) {
            errorDeleteUser && showDefaultNotification({ type: 'error', message: errorDeleteUser });
            setShowConfirmModal(false);
            setShowDeleteInfo(false);
            navigate(`/${SITE_URLS.myProfile}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime, succeeded, errorDeleteUser]);

    return (
        <MyAccountContainer title={translations.title}>
            <Container>
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Typography>{translations.intro}</Typography>

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Typography appearanceLevel={TypographyAppearanceVariants.level7} tagVariant={TypographyTagVariants.h2}>
                    {translations.sectionDeleteTitle}
                </Typography>
                <ul>
                    <li>{translations.sectionDeleteItem1}</li>
                    <li>{translations.sectionDeleteItem2}</li>
                    <li>{translations.sectionDeleteItem3}</li>
                </ul>

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Typography appearanceLevel={TypographyAppearanceVariants.level7} tagVariant={TypographyTagVariants.h2}>
                    {translations.sectionKeepTitle}
                </Typography>
                <Typography>{translations.sectionKeepText}</Typography>

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Typography appearanceLevel={TypographyAppearanceVariants.level7} tagVariant={TypographyTagVariants.h2}>
                    {translations.sectionBeforeTitle}
                </Typography>
                <ul>
                    <li>{translations.sectionBeforeItem1}</li>
                    <li>{translations.sectionBeforeItem2}</li>
                    <li>{translations.sectionBeforeItem3}</li>
                </ul>

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Typography>{translations.sectionAfterText1}</Typography>

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Typography>{translations.sectionAfterText2}</Typography>

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Button label={translations.buttonDelete} mode={ButtonMode.primary} onClick={() => setShowDeleteInfo(true)} />

                <Popup id="delete-account-info" show={showDeleteInfo} onHide={() => setShowDeleteInfo(false)} desktopWidth="400px">
                    <div className={`${modalStyles.content} d-flex flex-column gap-3`}>
                        <div className={`${modalStyles['modal-icon']} ${modalStyles['modal-icon-danger']}`}>
                            <div className={`d-flex flex-column justify-content-center text-center ${modalStyles['rounded-circle']}`}>
                                <i className="fa-thin fa-trash-can" aria-hidden="true" />
                            </div>
                        </div>
                        <Typography tagVariant={TypographyTagVariants.p} classes="mb-0" fontSize="20" fontWeight="900">
                            {translations.modalTitle}
                        </Typography>
                        <div>
                            {/* Pending orders count */}
                            <div className={modalStyles['issue-item']}>
                                <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                    {`${translations.modalItemPendingOrders} - ${pendingOrdersCount ?? translations.modalLoading}`}
                                </Typography>
                                <div className={modalStyles['issue-underline']} />
                            </div>

                            {/* Associated stores: each as an issue line */}
                            {(storeNames === null || isLoadingAllMyAccountStores) && (
                                <div className={modalStyles['issue-item']}>
                                    <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                        {`${translations.modalItemAssociatedStores} - ${translations.modalLoading}`}
                                    </Typography>
                                    <div className={modalStyles['issue-underline']} />
                                </div>
                            )}
                            {storeNames !== null && !isLoadingAllMyAccountStores && storeNames.length === 0 && (
                                <div className={modalStyles['issue-item']}>
                                    <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                        {`${translations.modalItemAssociatedStores} - ${translations.modalNoStores}`}
                                    </Typography>
                                    <div className={modalStyles['issue-underline']} />
                                </div>
                            )}
                            {storeNames !== null && !isLoadingAllMyAccountStores && storeNames.length > 0 && (
                                <>
                                    {storeNames.map((name) => (
                                        <div key={name} className={modalStyles['issue-item']}>
                                            <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                                {name}
                                            </Typography>
                                            <div className={modalStyles['issue-underline']} />
                                        </div>
                                    ))}
                                </>
                            )}

                            {/* Products count */}
                            <div className={modalStyles['issue-item']}>
                                <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                    {`${translations.modalItemProductCount} - ${productsCount ?? translations.modalLoading}`}
                                </Typography>
                                <div className={modalStyles['issue-underline']} />
                            </div>

                            {/* Services count */}
                            <div className={modalStyles['issue-item']}>
                                <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                    {`${translations.modalItemServiceCount} - ${servicesCount ?? translations.modalLoading}`}
                                </Typography>
                                <div className={modalStyles['issue-underline']} />
                            </div>
                        </div>
                        <div className="d-flex mt-3">
                            <Button
                                label={checkingIssues ? translations.modalLoading : translations.modalContinue}
                                mode={ButtonMode.primary}
                                classes="w-100"
                                onClick={onContinueFromInfo}
                                disabled={checkingIssues}
                            />
                        </div>
                    </div>
                </Popup>
                {/* Issues Modal */}
                <Popup id="delete-account-issues" show={showIssuesModal} onHide={() => setShowIssuesModal(false)} desktopWidth="480px">
                    <div className={`${modalStyles.content} d-flex flex-column gap-3`}>
                        <div className={`${modalStyles['modal-icon']} ${modalStyles['modal-icon-danger']}`}>
                            <div className={`d-flex flex-column justify-content-center text-center ${modalStyles['rounded-circle']}`}>
                                <i className="fa-thin fa-triangle-exclamation" aria-hidden="true" />
                            </div>
                        </div>
                        <Typography tagVariant={TypographyTagVariants.p} classes="mb-0" fontSize="20" fontWeight="900">
                            {translations.issuesTitle(
                                (pendingOrdersCount || 0) + (consumerOrdersCount || 0) + (consumerReservationsCount || 0)
                            )}
                        </Typography>
                        <Typography tagVariant={TypographyTagVariants.p} classes="mb-0" fontSize="16">
                            {translations.issuesSubtitle}
                        </Typography>
                        <div>
                            {(pendingOrdersCount || 0) > 0 && (
                                <div className={modalStyles['issue-item']}>
                                    <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                        {`${translations.issuesOrdersAsMerchant} - ${pendingOrdersCount ?? 0}`}
                                    </Typography>
                                    <div className={modalStyles['issue-underline']} />
                                </div>
                            )}
                            {(consumerOrdersCount || 0) > 0 && (
                                <div className={modalStyles['issue-item']}>
                                    <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                        {`${translations.issuesOrdersAsConsumer} - ${consumerOrdersCount ?? 0}`}
                                    </Typography>
                                    <div className={modalStyles['issue-underline']} />
                                </div>
                            )}
                            {(consumerReservationsCount || 0) > 0 && (
                                <div className={modalStyles['issue-item']}>
                                    <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                        {`${translations.issuesReservationsAsConsumer} - ${consumerReservationsCount ?? 0}`}
                                    </Typography>
                                    <div className={modalStyles['issue-underline']} />
                                </div>
                            )}
                        </div>
                        <div className="d-flex mt-3">
                            <Button label={translations.issuesBack} mode={ButtonMode.primary} classes="w-100" onClick={() => navigate(`/${SITE_URLS.myProfile}`)} />
                        </div>
                    </div>
                </Popup>

                {/* Confirm Deletion Modal */}
                <Popup id="delete-account-confirm" show={showConfirmModal} onHide={() => setShowConfirmModal(false)} desktopWidth="520px">
                    <div className={`${modalStyles.content} d-flex flex-column gap-3`}>
                        <div className={`${modalStyles['modal-icon']} ${modalStyles['modal-icon-danger']}`}>
                            <div className={`d-flex flex-column justify-content-center text-center ${modalStyles['rounded-circle']}`}>
                                <i className="fa-thin fa-trash-can" aria-hidden="true" />
                            </div>
                        </div>
                        <Typography tagVariant={TypographyTagVariants.p} classes="mb-0" fontSize="20" fontWeight="900">
                            {translations.confirmTitle}
                        </Typography>
                        <Typography tagVariant={TypographyTagVariants.p} classes="mb-0" fontSize="16">
                            {translations.confirmText}
                        </Typography>
                        <Typography tagVariant={TypographyTagVariants.p} classes="mb-0" fontSize="12" fontWeight="800" color="danger">
                            {translations.confirmWarning}
                        </Typography>
                        <div className="form-check mt-1">
                            <input
                                id="acknowledge-checkbox"
                                className="form-check-input"
                                type="checkbox"
                                checked={confirmAcknowledge}
                                onChange={(e) => setConfirmAcknowledge(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="acknowledge-checkbox">
                                {translations.confirmAcknowledge}
                            </label>
                        </div>
                        <div>
                            {/* Pending orders count */}
                            <div className={modalStyles['issue-item']}>
                                <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                    {`${translations.modalItemPendingOrders} - ${pendingOrdersCount ?? translations.modalLoading}`}
                                </Typography>
                                <div className={modalStyles['issue-underline']} />
                            </div>

                            {/* Associated stores: each as an issue line */}
                            {(storeNames === null || isLoadingAllMyAccountStores) && (
                                <div className={modalStyles['issue-item']}>
                                    <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                        {`${translations.modalItemAssociatedStores} - ${translations.modalLoading}`}
                                    </Typography>
                                    <div className={modalStyles['issue-underline']} />
                                </div>
                            )}
                            {storeNames !== null && !isLoadingAllMyAccountStores && storeNames.length === 0 && (
                                <div className={modalStyles['issue-item']}>
                                    <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                        {`${translations.modalItemAssociatedStores} - ${translations.modalNoStores}`}
                                    </Typography>
                                    <div className={modalStyles['issue-underline']} />
                                </div>
                            )}
                            {storeNames !== null && !isLoadingAllMyAccountStores && storeNames.length > 0 && (
                                <>
                                    {storeNames.map((name) => (
                                        <div key={name} className={modalStyles['issue-item']}>
                                            <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                                {name}
                                            </Typography>
                                            <div className={modalStyles['issue-underline']} />
                                        </div>
                                    ))}
                                </>
                            )}

                            {/* Products count */}
                            <div className={modalStyles['issue-item']}>
                                <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                    {`${translations.modalItemProductCount} - ${productsCount ?? translations.modalLoading}`}
                                </Typography>
                                <div className={modalStyles['issue-underline']} />
                            </div>

                            {/* Services count */}
                            <div className={modalStyles['issue-item']}>
                                <Typography tagVariant={TypographyTagVariants.p} classes="mb-1" fontSize="16">
                                    {`${translations.modalItemServiceCount} - ${servicesCount ?? translations.modalLoading}`}
                                </Typography>
                                <div className={modalStyles['issue-underline']} />
                            </div>
                        </div>
                        <div className="d-flex gap-3 mt-3">
                            <Button label={translations.confirmCancel} mode={ButtonMode.danger} classes="w-100" onClick={() => navigate(`/${SITE_URLS.myProfile}`)} />
                            <Button
                                label={translations.confirmCTA}
                                mode={ButtonMode.primary}
                                classes="w-100"
                                onClick={() => {
                                    requestDeleteUser();
                                }}
                                disabled={!confirmAcknowledge || loadingDeleteUser}
                            />
                        </div>
                    </div>
                </Popup>
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
            </Container>
        </MyAccountContainer>
    );
};
